import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import Cookies from "universal-cookie";
import APPLICATION_SETTINGS from "../../settings";

const API_BASE = APPLICATION_SETTINGS.VITE_API_BASE || "http://127.0.0.1:8002";
const backendHost = API_BASE.replace(/^https?:\/\//, "")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const [globalWsConnected, setGlobalWsConnected] = useState(false);
  const [globalWsError, setGlobalWsError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [threadUnreadCounts, setThreadUnreadCounts] = useState({});
  const [realTimeMessages, setRealTimeMessages] = useState({});
  const [messageListTyping, setMessageListTyping] = useState({});
  const [newThreads, setNewThreads] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentSelectedThreadId, setCurrentSelectedThreadId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const globalWsRef = useRef(null);
  const cookies = new Cookies();
  const token = cookies.get("token");

  // Function to refresh message list from API
  const refreshMessageList = useCallback(async () => {
    if (!token) return [];

    try {
      const response = await fetch(`${API_BASE}/api/chat/threads/`, {
        method: "GET",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.results || data || [];
      }
    } catch (error) {
      console.error("Error refreshing message list:", error);
    }
    return [];
  }, [token]);

  // Store refreshMessageList in a ref to avoid dependency issues
  const refreshMessageListRef = useRef(refreshMessageList);
  refreshMessageListRef.current = refreshMessageList;

  // Function to update unread count
  const updateUnreadCount = useCallback(
    (threadId, count, isIncrement = true) => {
      if (isIncrement) {
        setUnreadCount((prev) => prev + count);
        setThreadUnreadCounts((prev) => ({
          ...prev,
          [threadId]: (prev[threadId] || 0) + count,
        }));
      } else {
        setUnreadCount((prev) => Math.max(0, prev - count));
        setThreadUnreadCounts((prev) => {
          const newCounts = { ...prev };
          if (newCounts[threadId] <= count) {
            delete newCounts[threadId];
          } else {
            newCounts[threadId] = newCounts[threadId] - count;
          }
          return newCounts;
        });
      }
    },
    []
  );

  // Function to clear unread count for a thread
  const clearThreadUnreadCount = useCallback(
    (threadId) => {
      const currentCount = threadUnreadCounts[threadId] || 0;
      if (currentCount > 0) {
        setUnreadCount((prev) => Math.max(0, prev - currentCount));
        setThreadUnreadCounts((prev) => {
          const newCounts = { ...prev };
          delete newCounts[threadId];
          return newCounts;
        });
      }
    },
    [threadUnreadCounts]
  );

  // Function to set current user ID
  const setCurrentUser = useCallback((userId) => {
    setCurrentUserId(userId);
  }, []);

  // Function to set currently selected thread ID
  const setSelectedThread = useCallback((threadId) => {
    setCurrentSelectedThreadId(threadId);
  }, []);

  // Global WebSocket connection for message list updates
  useEffect(() => {
    if (!token) return;

    // 1. Determine protocol: if API is https, WS must be wss
    const protocol = API_BASE.startsWith("https") ? "wss:" : "ws:";

    // 2. Always use the backendHost derived from API_BASE
    // (backendHost is defined at the top of the file)
    const host = backendHost;

    const tokenQS = token ? `?token=${encodeURIComponent(token)}` : "";
    const globalWsUrl = `${protocol}//${host}/ws/chat/notifications/${tokenQS}`;

    console.log("[Global Notifications WS] connecting:", globalWsUrl);
    setGlobalWsConnected(false);
    setGlobalWsError(null);

    const globalWs = new WebSocket(globalWsUrl);
    globalWsRef.current = globalWs;

    // Connection timeout
    const connectTimer = setTimeout(() => {
      if (
        globalWsRef.current === globalWs &&
        globalWs.readyState !== WebSocket.OPEN
      ) {
        console.warn("[Global WS] connection timeout");
        setGlobalWsError("Connection timeout");
        try {
          globalWs.close();
        } catch {}
      }
    }, 8000);

    globalWs.onopen = () => {
      console.log(
        "[Global Notifications WS] connected successfully to:",
        globalWsUrl
      );
      clearTimeout(connectTimer);
      setGlobalWsConnected(true);
    };

    globalWs.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        console.log("[Global Notifications WS] Received:", payload);

        // Handle payloads without type field (minimal notifications)
        if (!payload.type && payload.thread_id) {
          // Do NOT increment if user is currently viewing this thread
          if (payload.thread_id !== currentSelectedThreadId) {
            setUnreadCount((prev) => prev + 1);
            setThreadUnreadCounts((prev) => ({
              ...prev,
              [payload.thread_id]: (prev[payload.thread_id] || 0) + 1,
            }));
          }
          refreshMessageListRef.current();
          return;
        }

        switch (payload.type) {
          case "new_message":
          case "message_notification":
          case "thread_update":
            // Update real-time messages for the message list
            const messageBody =
              payload.data?.body ||
              payload.message_body ||
              payload.body ||
              "New message";
            const senderName =
              payload.data?.sender_name || payload.sender_name || "Unknown";
            const senderId = payload.data?.sender_id || payload.sender_id;
            const timestamp =
              payload.data?.created_at ||
              payload.timestamp ||
              new Date().toISOString();

            console.log("[Global Notifications WS] Extracted message data:", {
              messageBody,
              senderName,
              senderId,
              timestamp,
              threadId: payload.thread_id,
            });

            setRealTimeMessages((prev) => {
              const existing = prev[payload.thread_id];
              // Only update if the message is actually new or different
              if (
                !existing ||
                existing.body !== messageBody ||
                existing.sender_id !== senderId ||
                existing.timestamp !== timestamp
              ) {
                return {
                  ...prev,
                  [payload.thread_id]: {
                    body: messageBody,
                    sender_name: senderName,
                    timestamp: timestamp,
                    sender_id: senderId,
                  },
                };
              }
              return prev;
            });

            // Update unread count if message is not from current user and not for currently viewed chat
            if (
              senderId &&
              senderId !== currentUserId &&
              payload.thread_id !== currentSelectedThreadId
            ) {
              updateUnreadCount(payload.thread_id, 1);
            } else if (
              payload.thread_id &&
              !senderId &&
              payload.thread_id !== currentSelectedThreadId
            ) {
              // For minimal payloads, only increment if not currently viewing this chat
              updateUnreadCount(payload.thread_id, 1);
            }

            // Always refresh message list for new_message type to ensure UI updates
            console.log(
              "[Global Notifications WS] Calling refreshMessageList for new_message"
            );
            refreshMessageListRef.current();
            // Trigger refresh for components listening to this event
            setRefreshTrigger((prev) => prev + 1);
            break;

          case "new_thread":
            console.log(
              "[Global Notifications WS] New thread created:",
              payload
            );
            // Add new thread to the messages list in real-time
            setNewThreads((prev) => {
              const newThread = {
                id: payload.thread_id,
                threadId: payload.thread_id,
                name: payload.other_user_name || "New User",
                last_message: {
                  body: payload.first_message || "New conversation started",
                  sender_name: payload.other_user_name || "New User",
                  sender_id: payload.other_user_id,
                  created_at: new Date().toISOString(),
                },
                last_message_at: new Date().toISOString(),
                timestamp: new Date().toISOString(),
                issuetype: payload.issue_type || "General",
                hasOffer: false,
                hasJob: false,
                isOnline: true,
              };
              return [newThread, ...prev];
            });
            break;

          case "unread_count_update":
            console.log(
              "[Global Notifications WS] Unread count update:",
              payload
            );
            setUnreadCount(payload.count || 0);
            break;

          case "thread_unread_count":
            console.log(
              "[Global Notifications WS] Thread unread count update:",
              payload
            );
            if (payload.thread_id && payload.count !== undefined) {
              setThreadUnreadCounts((prev) => ({
                ...prev,
                [payload.thread_id]: payload.count,
              }));
            }
            break;

          case "user_typing":
            setMessageListTyping((prev) => ({
              ...prev,
              [payload.thread_id]: {
                user_id: payload.user_id,
                user_name: payload.user_name || "Someone",
                isTyping: true,
              },
            }));
            break;

          case "user_stopped_typing":
            setMessageListTyping((prev) => {
              const c = { ...prev };
              delete c[payload.thread_id];
              return c;
            });
            break;

          case "unread_counts":
            console.log(
              "[Global Notifications WS] Unread counts received:",
              payload
            );
            // Handle bulk unread counts update from server
            if (payload.threads && Array.isArray(payload.threads)) {
              const newThreadUnreadCounts = {};
              let totalUnreadCount = 0;

              payload.threads.forEach((thread) => {
                if (thread.thread_id && thread.unread_count !== undefined) {
                  newThreadUnreadCounts[thread.thread_id] = thread.unread_count;
                  totalUnreadCount += thread.unread_count;
                  console.log(
                    `[Global Notifications WS] Thread ${thread.thread_id}: ${thread.unread_count} unread`
                  );
                }
              });

              console.log(
                "[Global Notifications WS] Setting threadUnreadCounts to:",
                newThreadUnreadCounts
              );
              setThreadUnreadCounts(newThreadUnreadCounts);
              setUnreadCount(totalUnreadCount);

              console.log(
                "[Global Notifications WS] Updated thread unread counts:",
                newThreadUnreadCounts,
                "Total unread:",
                totalUnreadCount
              );
            }
            break;

          case "connection_status":
            console.log(
              "[Global Notifications WS] Connection status:",
              payload
            );
            break;

          default:
            console.log(
              "[Global Notifications WS] Unknown message type:",
              payload.type
            );
        }
      } catch (error) {
        console.error(
          "[Global Notifications WS] Error parsing message:",
          error
        );
      }
    };

    globalWs.onerror = (e) => {
      console.error("[Global Notifications WS] error", e);
      console.error(
        "[Global Notifications WS] Failed to connect to:",
        globalWsUrl
      );
      clearTimeout(connectTimer);
      setGlobalWsConnected(false);
      setGlobalWsError("WebSocket error");
    };

    globalWs.onclose = (ev) => {
      console.log("[Global Notifications WS] close", ev.code, ev.reason);
      console.log(
        "[Global Notifications WS] Connection closed for:",
        globalWsUrl
      );
      clearTimeout(connectTimer);
      setGlobalWsConnected(false);
      if (ev.code === 4401)
        setGlobalWsError("Unauthorized (token/session invalid)");
      else if (ev.code === 4403) setGlobalWsError("Forbidden");
      else if (ev.code === 4400) setGlobalWsError("Bad request");
      else if (ev.code === 1006) setGlobalWsError("Connection failed");
    };

    return () => {
      clearTimeout(connectTimer);
      if (globalWsRef.current === globalWs) {
        try {
          globalWs.close();
        } catch {}
        globalWsRef.current = null;
      }
    };
  }, [token, updateUnreadCount, currentUserId, currentSelectedThreadId]);

  // Cleanup WebSocket connections on component unmount
  useEffect(() => {
    return () => {
      if (globalWsRef.current) {
        globalWsRef.current.close();
        globalWsRef.current = null;
      }
    };
  }, []);

  // Cleanup old real-time message data to prevent timestamp issues
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = new Date();
      setRealTimeMessages((prev) => {
        const cleaned = {};
        Object.keys(prev).forEach((threadId) => {
          const realTimeData = prev[threadId];
          if (realTimeData?.timestamp) {
            const messageDate = new Date(realTimeData.timestamp);
            const diffInMinutes = (now - messageDate) / (1000 * 60);

            // Keep real-time data only if it's recent (within last 5 minutes)
            if (diffInMinutes < 5) {
              cleaned[threadId] = realTimeData;
            }
          }
        });
        return cleaned;
      });
    }, 30000); // Clean up every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, []);

  const value = {
    // Connection status
    globalWsConnected,
    globalWsError,

    // Unread counts
    unreadCount,
    setUnreadCount,
    threadUnreadCounts,
    setThreadUnreadCounts,
    updateUnreadCount,
    clearThreadUnreadCount,

    // Real-time data
    realTimeMessages,
    setRealTimeMessages,
    messageListTyping,
    setMessageListTyping,
    newThreads,
    setNewThreads,

    // Current user and thread tracking
    currentUserId,
    currentSelectedThreadId,
    setCurrentUser,
    setSelectedThread,

    // Utility functions
    refreshMessageList,
    refreshTrigger,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use NotificationContext
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export default NotificationProvider;
